import { useState } from 'react';
import { motion } from 'framer-motion';
import { Utensils, MapPin, Users, Camera, Zap, Ticket, Minus, Plus } from 'lucide-react';

const features = [
  {
    icon: Utensils,
    title: 'Breakfast & Restaurant Lunch',
    description: 'Start your morning with a complimentary breakfast and enjoy a proper sit-down lunch at an American restaurant. This is real dining, not fast food.',
    color: '#FF007A',
  },
  {
    icon: MapPin,
    title: 'Free Hotel Pickup & Drop-off',
    description: 'We pick you up directly from over 100 Las Vegas hotels on the Strip, Downtown, and surrounding areas. Zero stress, zero planning needed.',
    color: '#00E5FF',
  },
  {
    icon: Users,
    title: 'Small Groups & Private Tours',
    description: 'Travel in intimate groups aboard luxury vehicles. Want an exclusive experience? Book a private tour with a fully customizable itinerary.',
    color: '#9D00FF',
  },
  {
    icon: Camera,
    title: 'Iconic Photo Opportunities',
    description: 'Capture memories at every legendary stop. From the Hollywood Sign to Rodeo Drive, you will leave with a camera roll full of incredible shots.',
    color: '#FFD600',
  },
  {
    icon: Zap,
    title: 'Instant Booking Confirmation',
    description: 'Reserve your spot online in seconds and receive immediate confirmation. Our 24/7 reservation team is available for last-minute bookings anytime.',
    color: '#FF007A',
  },
];

const GroupCoupon = () => {
  const [guests, setGuests] = useState(2);
  const price = guests >= 15 ? 185 : 215;
  const total = price * guests;
  const savings = guests >= 15 ? (215 - 185) * guests : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.5 }}
      className="bg-[#FFD600] border-2 border-black rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6 lg:p-8 flex flex-col"
      data-testid="group-coupon-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Ticket className="w-6 h-6 text-[#FF007A]" />
        <h3 className="font-bebas text-xl sm:text-2xl tracking-tight uppercase">Group Savings Calculator</h3>
      </div>

      <p className="font-outfit text-sm text-black/70 mb-4">
        See how much your group saves. Groups of 15+ unlock special pricing!
      </p>

      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={() => setGuests(Math.max(1, guests - 1))}
          className="w-10 h-10 rounded-full border-2 border-black bg-white flex items-center justify-center hover:bg-gray-100 transition-colors"
          data-testid="coupon-minus"
        >
          <Minus className="w-4 h-4" />
        </button>
        <div className="text-center">
          <span className="font-bebas text-4xl text-[#FF007A]" data-testid="coupon-guest-count">{guests}</span>
          <p className="font-outfit text-xs uppercase tracking-wider font-bold">Guests</p>
        </div>
        <button
          onClick={() => setGuests(Math.min(55, guests + 1))}
          className="w-10 h-10 rounded-full border-2 border-black bg-white flex items-center justify-center hover:bg-gray-100 transition-colors"
          data-testid="coupon-plus"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white/80 rounded-lg border-2 border-black p-3 mb-3 text-center">
        <p className="font-outfit text-xs uppercase tracking-wider font-bold text-black/60">
          {guests >= 15 ? 'Group Rate' : 'Standard Rate'}
        </p>
        <p className="font-bebas text-3xl text-[#FF007A]" data-testid="coupon-price">${price}<span className="text-lg text-black/60">/person</span></p>
        <p className="font-outfit text-sm font-bold">Total: ${total}</p>
        {savings > 0 && (
          <p className="font-outfit text-xs text-green-600 font-bold mt-1" data-testid="coupon-savings">
            You save ${savings} with group pricing!
          </p>
        )}
      </div>

      {guests < 15 && (
        <p className="font-outfit text-xs text-black/60 text-center mb-3">
          Add {15 - guests} more guest{15 - guests > 1 ? 's' : ''} to unlock <strong>$185/person</strong> group rate!
        </p>
      )}

      <p className="font-outfit text-xs text-black/50 text-center mb-3">
        Children under 2 ride free on private bookings only (max 2 kids per booking).
      </p>

      <a
        href="#booking"
        onClick={(e) => { e.preventDefault(); window.location.hash = 'booking'; }}
        className="btn-primary px-6 py-2.5 text-sm text-center mt-auto"
        data-testid="coupon-book-cta"
      >
        BOOK {guests} GUEST{guests > 1 ? 'S' : ''} NOW
      </a>
    </motion.div>
  );
};

const Features = () => {
  return (
    <section id="features" className="section-padding bg-[#FDFBF7]" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FF007A] mb-2">Why Travelers Choose Us</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">
            The Ultimate Hollywood Experience
          </h2>
          <p className="font-outfit text-base sm:text-lg text-[#4A4A4A] max-w-2xl mx-auto mt-4">
            Everything you need for the best full-day guided sightseeing tour from Las Vegas to Hollywood, California.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card-brutal p-6 lg:p-8"
              data-testid={`feature-card-${i}`}
            >
              <div
                className="w-12 h-12 rounded-lg border-2 border-black flex items-center justify-center mb-4"
                style={{ backgroundColor: feature.color + '20' }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              <h3 className="font-bebas text-xl sm:text-2xl tracking-tight uppercase mb-2">{feature.title}</h3>
              <p className="font-outfit text-sm sm:text-base text-[#4A4A4A] leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
          <GroupCoupon />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <a 
            href="#booking" 
            onClick={(e) => { e.preventDefault(); window.location.hash = 'booking'; }}
            className="btn-primary px-8 py-4 text-base" 
            data-testid="features-book-cta"
          >
            RESERVE YOUR SPOT - $215/PERSON
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
